import React, { useState, useEffect } from 'react';
import Select, { OnChangeValue } from 'react-select';
import { searchCompanies, searchCompaniesByTickerPrefix } from '../../api/companyApi';
import { Company } from '../../store/company/types';
import styles from './TickerInput.module.scss';

interface TickerOption {
  value: Company;
  label: string;
}

type TickerInputProps =
  | {
      isMulti?: false;
      value: Company | null;
      onChange: (company: Company | null) => void;
      placeholder?: string;
      embeddedOnly?: boolean;
      noOptionsMessage?: string;
    }
  | {
      isMulti: true;
      value: Company[];
      onChange: (companies: Company[]) => void;
      placeholder?: string;
      embeddedOnly?: boolean;
      noOptionsMessage?: string;
    };

const TickerInput: React.FC<TickerInputProps> = (props) => {
  const { placeholder = 'Search by ticker or name' } = props;
  const [options, setOptions] = useState<TickerOption[]>([]);
  const [inputValue, setInputValue] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Parse the current value into selected option
  const selectedOption = (() => {
    if (props.isMulti) {
      return props.value.map((company) => ({
        value: company,
        label: `${company.ticker} - ${company.name}`,
      }));
    }
    return props.value
      ? { value: props.value, label: `${props.value.ticker} - ${props.value.name}` }
      : null;
  })();

  // Fetch companies when input changes
  useEffect(() => {
    const fetchCompanies = async () => {
      const input = inputValue.trim();
      if (input.length < 1) return;

      setIsLoading(true);
      try {
        let companies: Company[];
        if (input.startsWith('$')) {
          companies = await searchCompaniesByTickerPrefix(input.substring(1), {
            embeddedOnly: props.embeddedOnly,
          });
        } else {
          companies = await searchCompanies(input, { embeddedOnly: props.embeddedOnly });
        }

        const newOptions = companies.map((company: Company) => ({
          value: company,
          label: `${company.ticker} - ${company.name}`,
        }));
        setOptions(newOptions);
      } catch (error) {
        console.error('Error fetching companies:', error);
      } finally {
        setIsLoading(false);
      }
    };

    const timer = setTimeout(() => {
      fetchCompanies();
    }, 300);

    return () => clearTimeout(timer);
  }, [inputValue]);

  const handleChange = (selected: OnChangeValue<TickerOption, boolean>) => {
    if (props.isMulti) {
      props.onChange(selected ? (selected as TickerOption[]).map((option) => option.value) : []);
    } else {
      props.onChange(selected ? (selected as TickerOption).value : null);
    }
  };

  const handleInputChange = (newValue: string) => {
    setInputValue(newValue);
  };

  const selectId = 'ticker-input';

  return (
    <div className={styles.tickerInput}>
      <Select
        inputId={selectId}
        isMulti={props.isMulti}
        isClearable
        options={options}
        value={selectedOption}
        onChange={handleChange}
        onInputChange={handleInputChange}
        isLoading={isLoading}
        placeholder={placeholder}
        noOptionsMessage={() =>
          inputValue.length < 1
            ? 'Type at least 1 character to search'
            : props.noOptionsMessage ||
              (props.embeddedOnly
                ? "No companies found. This company can't be searched yet."
                : 'No companies found, we may not have data for it yet')
        }
        className={styles.select}
        classNamePrefix="ticker-select"
        filterOption={null}
      />
    </div>
  );
};

export default TickerInput;
